import { useEffect, useRef, useState } from 'react'
import { supabase } from './supabaseClient'
import './MyPage.css'

const GRAD_YEARS = [2025, 2026, 2027, 2028, 2029, 2030]
const POSITIONS  = ['P', 'C', '1B', '2B', '3B', 'SS', 'OF', 'UTIL', 'DH']

const EMPTY_FORM = {
  full_name:         '',
  grad_year:         '',
  position:          '',
  high_school:       '',
  hometown:          '',
  gpa:               '',
  sat_score:         'N/A',
  act_score:         'N/A',
  height:            '',
  weight:            '',
  bats:              '',
  throws:            '',
  bio:               '',
  perfect_game_url:  '',
  prep_baseball_url: '',
  video_url:         '',
  avatar_url:        '',
  instagram:         '',
  twitter:           '',
  is_public:         true,
  slug:              '',
  hs_coach_first_name:     '',
  hs_coach_last_name:      '',
  hs_coach_email:          '',
  hs_coach_phone:          '',
  travel_coach_first_name: '',
  travel_coach_last_name:  '',
  travel_coach_email:      '',
  travel_coach_phone:      '',
  transcript_url:          '',
  contact_email:           '',
}

function generateSlug(name) {
  return name.toLowerCase().trim()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

function avatarInitials(name) {
  return (name || '').trim().split(/\s+/).map(w => w[0] ?? '').join('').toUpperCase().slice(0, 2) || '?'
}

export default function MyPage({ user }) {
  useEffect(() => { document.title = 'My Page — CommitHQ' }, [])

  const [form, setForm]           = useState(EMPTY_FORM)
  const [loading, setLoading]     = useState(true)
  const [saving, setSaving]       = useState(false)
  const [toast, setToast]         = useState(null)
  const [saveError, setSaveError] = useState(null)
  const savedHometownRef          = useRef('')
  const [copied, setCopied]       = useState(false)

  // Video upload state
  const [uploading, setUploading]           = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError]       = useState(null)
  const [videoFileName, setVideoFileName]   = useState(null)
  const [removingVideo, setRemovingVideo]   = useState(false)
  const videoInputRef = useRef(null)

  // Avatar upload state
  const [avatarUploading, setAvatarUploading]       = useState(false)
  const [avatarUploadProgress, setAvatarUploadProgress] = useState(0)
  const [avatarError, setAvatarError]               = useState(null)
  const [removingAvatar, setRemovingAvatar]         = useState(false)
  const avatarInputRef = useRef(null)

  // Transcript upload state
  const [transcriptUploading, setTranscriptUploading]       = useState(false)
  const [transcriptUploadProgress, setTranscriptUploadProgress] = useState(0)
  const [transcriptError, setTranscriptError]               = useState(null)
  const [removingTranscript, setRemovingTranscript]         = useState(false)
  const transcriptInputRef = useRef(null)

  useEffect(() => {
    async function loadProfile() {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()
      if (data) {
        setForm(prev => ({
          ...prev,
          // Replace DB nulls with empty-string defaults so .length / controlled inputs never crash
          ...Object.fromEntries(
            Object.entries(data).map(([k, v]) => [k, v === null && prev[k] === '' ? '' : v])
          ),
          sat_score: data.sat_score || 'N/A',
          act_score: data.act_score || 'N/A',
        }))
        savedHometownRef.current = data.hometown || ''
      }
      setLoading(false)
    }
    loadProfile()
  }, [user.id])

  function handleChange(e) {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  function togglePublic() {
    setForm(prev => ({ ...prev, is_public: !prev.is_public }))
  }

  async function handleSave() {
    setSaving(true); setSaveError(null)
    const slug = form.slug || (form.full_name.trim() ? generateSlug(form.full_name) : '')
    const { error } = await supabase
      .from('profiles')
      .upsert({ ...form, id: user.id, slug, updated_at: new Date().toISOString() }, { onConflict: 'id' })
    if (error) {
      setSaveError(error.message)
    } else {
      if (!form.slug && slug) setForm(prev => ({ ...prev, slug }))
      setToast('Profile saved!')
      // Geocode hometown if missing or changed since last save
      const hometownChanged = form.hometown !== savedHometownRef.current
      if (form.hometown && (!form.home_lat || hometownChanged)) {
        try {
          const res  = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(form.hometown)}`)
          const json = await res.json()
          if (json?.[0]) {
            const lat = parseFloat(json[0].lat)
            const lng = parseFloat(json[0].lon)
            await supabase.from('profiles').update({ home_lat: lat, home_lng: lng }).eq('id', user.id)
            setForm(prev => ({ ...prev, home_lat: lat, home_lng: lng }))
            console.log('Saved hometown coordinates', form.hometown, lat, lng)
          }
        } catch { /* non-critical */ }
      }
      savedHometownRef.current = form.hometown
    }
    setSaving(false)
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(`commithq.app/athlete/${form.slug}`)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* ignore */ }
  }

  // ── Video upload ────────────────────────────────────────────────

  async function handleVideoUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError(null)
    if (file.size > 261_888_000) {
      setUploadError('Video must be under 250MB')
      if (videoInputRef.current) videoInputRef.current.value = ''
      return
    }
    setUploading(true)
    setUploadProgress(0)

    const filePath = `${user.id}/highlight.mp4`

    const { error: uploadErr } = await supabase.storage
      .from('athlete-videos')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
        onUploadProgress: (p) => {
          setUploadProgress(Math.round((p.loaded / p.total) * 100))
        },
      })

    if (uploadErr) {
      setUploadError(uploadErr.message)
      setUploading(false)
      if (videoInputRef.current) videoInputRef.current.value = ''
      return
    }

    const { data: urlData } = supabase.storage
      .from('athlete-videos')
      .getPublicUrl(filePath)

    const publicUrl = urlData.publicUrl

    // Immediately persist video_url to DB
    await supabase
      .from('profiles')
      .upsert({ id: user.id, video_url: publicUrl, updated_at: new Date().toISOString() }, { onConflict: 'id' })

    setForm(prev => ({ ...prev, video_url: publicUrl }))
    setVideoFileName(file.name)
    setUploading(false)
    setUploadProgress(0)
    if (videoInputRef.current) videoInputRef.current.value = ''
  }

  async function handleRemoveVideo() {
    setRemovingVideo(true)
    const filePath = `${user.id}/highlight.mp4`

    const [storageRes, dbRes] = await Promise.all([
      supabase.storage.from('athlete-videos').remove([filePath]),
      supabase.from('profiles').update({ video_url: null, updated_at: new Date().toISOString() }).eq('id', user.id),
    ])

    if (!storageRes.error && !dbRes.error) {
      setForm(prev => ({ ...prev, video_url: '' }))
      setVideoFileName(null)
    } else {
      const msg = storageRes.error?.message || dbRes.error?.message
      setUploadError(msg)
    }
    setRemovingVideo(false)
  }

  // ── Avatar upload ────────────────────────────────────────────────

  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Photo must be under 5MB.')
      if (avatarInputRef.current) avatarInputRef.current.value = ''
      return
    }
    setAvatarUploading(true)
    setAvatarUploadProgress(0)
    setAvatarError(null)

    const filePath = `${user.id}/avatar.jpg`

    const { error: uploadErr } = await supabase.storage
      .from('athlete-avatars')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
        onUploadProgress: (p) => {
          setAvatarUploadProgress(Math.round((p.loaded / p.total) * 100))
        },
      })

    if (uploadErr) {
      setAvatarError(uploadErr.message)
      setAvatarUploading(false)
      if (avatarInputRef.current) avatarInputRef.current.value = ''
      return
    }

    const { data: urlData } = supabase.storage
      .from('athlete-avatars')
      .getPublicUrl(filePath)

    // Cache-bust so the browser reloads the new image even if path is the same
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`

    await supabase
      .from('profiles')
      .upsert({ id: user.id, avatar_url: publicUrl, updated_at: new Date().toISOString() }, { onConflict: 'id' })

    setForm(prev => ({ ...prev, avatar_url: publicUrl }))
    setAvatarUploading(false)
    setAvatarUploadProgress(0)
    if (avatarInputRef.current) avatarInputRef.current.value = ''
  }

  async function handleRemoveAvatar() {
    setRemovingAvatar(true)
    const filePath = `${user.id}/avatar.jpg`

    const [storageRes, dbRes] = await Promise.all([
      supabase.storage.from('athlete-avatars').remove([filePath]),
      supabase.from('profiles').update({ avatar_url: null, updated_at: new Date().toISOString() }).eq('id', user.id),
    ])

    if (!storageRes.error && !dbRes.error) {
      setForm(prev => ({ ...prev, avatar_url: '' }))
    } else {
      setAvatarError(storageRes.error?.message || dbRes.error?.message)
    }
    setRemovingAvatar(false)
  }

  // ── Transcript upload ────────────────────────────────────────────

  async function handleTranscriptUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setTranscriptError(null)
    if (file.size > 10 * 1024 * 1024) {
      setTranscriptError('Transcript must be under 10MB.')
      if (transcriptInputRef.current) transcriptInputRef.current.value = ''
      return
    }
    setTranscriptUploading(true)
    setTranscriptUploadProgress(0)

    const filePath = `${user.id}/transcript.pdf`

    const { error: uploadErr } = await supabase.storage
      .from('athlete-transcripts')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
        onUploadProgress: (p) => {
          setTranscriptUploadProgress(Math.round((p.loaded / p.total) * 100))
        },
      })

    if (uploadErr) {
      setTranscriptError(uploadErr.message)
      setTranscriptUploading(false)
      if (transcriptInputRef.current) transcriptInputRef.current.value = ''
      return
    }

    const { data: urlData } = supabase.storage
      .from('athlete-transcripts')
      .getPublicUrl(filePath)

    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`

    await supabase
      .from('profiles')
      .upsert({ id: user.id, transcript_url: publicUrl, updated_at: new Date().toISOString() }, { onConflict: 'id' })

    setForm(prev => ({ ...prev, transcript_url: publicUrl }))
    setTranscriptUploading(false)
    setTranscriptUploadProgress(0)
    if (transcriptInputRef.current) transcriptInputRef.current.value = ''
  }

  async function handleRemoveTranscript() {
    setRemovingTranscript(true)
    const filePath = `${user.id}/transcript.pdf`

    const [storageRes, dbRes] = await Promise.all([
      supabase.storage.from('athlete-transcripts').remove([filePath]),
      supabase.from('profiles').update({ transcript_url: null, updated_at: new Date().toISOString() }).eq('id', user.id),
    ])

    if (!storageRes.error && !dbRes.error) {
      setForm(prev => ({ ...prev, transcript_url: '' }))
    } else {
      setTranscriptError(storageRes.error?.message || dbRes.error?.message)
    }
    setRemovingTranscript(false)
  }

  const displayedFileName = videoFileName || (form.video_url ? 'highlight.mp4' : null)

  if (loading) return <div className="mypage-loading">Loading profile…</div>

  return (
    <div className="mypage">

      {/* ──────────────────── LEFT: EDIT PANEL ──────────────────── */}
      <div className="mypage-edit">

        {/* Visibility toggle */}
        <div className="mypage-visibility">
          <span className={`visibility-label${form.is_public ? ' is-public' : ' is-private'}`}>
            {form.is_public ? (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                Profile is Public
              </>
            ) : (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Profile is Private
              </>
            )}
          </span>
          <button
            className={`toggle-switch${form.is_public ? ' toggle-on' : ''}`}
            onClick={togglePublic}
            role="switch"
            aria-checked={form.is_public}
            aria-label="Toggle profile visibility"
          >
            <span className="toggle-thumb" />
          </button>
        </div>

        {/* ── Your Info ── */}
        <div className="edit-section">
          <h3 className="edit-section-title">Your Info</h3>

          {/* Avatar upload */}
          <div className="avatar-upload-section">
            <div className="avatar-preview-wrap">
              {form.avatar_url ? (
                <img src={form.avatar_url} alt="Profile" className="avatar-preview-img" />
              ) : (
                <div className="avatar-preview-initials">
                  {avatarInitials(form.full_name)}
                </div>
              )}
              {avatarUploading && (
                <div className="avatar-uploading-overlay">
                  <span className="avatar-uploading-pct">{avatarUploadProgress > 0 ? `${avatarUploadProgress}%` : '…'}</span>
                </div>
              )}
            </div>
            <div className="avatar-upload-controls">
              <label className="btn-upload-avatar" htmlFor="avatar-file-input">
                {avatarUploading ? 'Uploading…' : form.avatar_url ? 'Change Photo' : 'Upload Photo'}
              </label>
              <input
                ref={avatarInputRef}
                id="avatar-file-input"
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={handleAvatarUpload}
                disabled={avatarUploading}
                style={{ display: 'none' }}
              />
              {form.avatar_url && (
                <button
                  className="btn-remove-avatar"
                  onClick={handleRemoveAvatar}
                  disabled={removingAvatar}
                >
                  {removingAvatar ? 'Removing…' : 'Remove'}
                </button>
              )}
            </div>
            {avatarError && <div className="avatar-upload-error">{avatarError}</div>}
          </div>

          <div className="edit-field">
            <label>Full Name</label>
            <input name="full_name" type="text" value={form.full_name} onChange={handleChange} placeholder="Jake Mitchell" />
          </div>
          <div className="edit-row">
            <div className="edit-field">
              <label>Grad Year</label>
              <select name="grad_year" value={form.grad_year} onChange={handleChange}>
                <option value="">—</option>
                {GRAD_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="edit-field">
              <label>Position</label>
              <select name="position" value={form.position} onChange={handleChange}>
                <option value="">—</option>
                {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="edit-field">
            <label>High School</label>
            <input name="high_school" type="text" value={form.high_school} onChange={handleChange} placeholder="Lincoln High School" />
          </div>
          <div className="edit-field">
            <label>Hometown</label>
            <input name="hometown" type="text" value={form.hometown} onChange={handleChange} placeholder="Dallas, TX" />
          </div>
          <div className="edit-field">
            <label>Contact Email</label>
            <input name="contact_email" type="text" value={form.contact_email} onChange={handleChange} placeholder="Email you want coaches to use" />
          </div>
        </div>

        {/* ── Academics ── */}
        <div className="edit-section">
          <h3 className="edit-section-title">Academics</h3>
          <div className="edit-row edit-row-3">
            <div className="edit-field">
              <label>GPA</label>
              <input name="gpa" type="text" value={form.gpa} onChange={handleChange} placeholder="3.8" />
            </div>
            <div className="edit-field">
              <label>SAT</label>
              <input name="sat_score" type="text" value={form.sat_score} onChange={handleChange} placeholder="1420" />
            </div>
            <div className="edit-field">
              <label>ACT</label>
              <input name="act_score" type="text" value={form.act_score} onChange={handleChange} placeholder="32" />
            </div>
          </div>

          {/* Transcript upload */}
          <div className="edit-field" style={{ marginTop: 14 }}>
            <label>Transcript</label>
            {form.transcript_url ? (
              <div className="transcript-uploaded">
                <span className="transcript-uploaded-indicator">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Transcript Uploaded ✓
                </span>
                <div className="transcript-uploaded-actions">
                  <a
                    href={form.transcript_url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-transcript-view"
                  >
                    View
                  </a>
                  <button
                    className="btn-transcript-remove"
                    onClick={handleRemoveTranscript}
                    disabled={removingTranscript}
                  >
                    {removingTranscript ? 'Removing…' : 'Remove'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="transcript-upload-wrap">
                <label className={`btn-upload-transcript${transcriptUploading ? ' uploading' : ''}`} htmlFor="transcript-file-input">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="12" y1="18" x2="12" y2="12"/>
                    <line x1="9" y1="15" x2="15" y2="15"/>
                  </svg>
                  {transcriptUploading ? 'Uploading…' : 'Upload Transcript'}
                </label>
                <input
                  ref={transcriptInputRef}
                  id="transcript-file-input"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleTranscriptUpload}
                  disabled={transcriptUploading}
                  style={{ display: 'none' }}
                />
                {transcriptUploading && (
                  <div className="transcript-progress-wrap">
                    <div className="transcript-progress-bar">
                      <div
                        className="transcript-progress-fill"
                        style={{ width: `${transcriptUploadProgress || 5}%` }}
                      />
                    </div>
                    <span className="transcript-progress-label">
                      {transcriptUploadProgress > 0 ? `${transcriptUploadProgress}%` : 'Uploading…'}
                    </span>
                  </div>
                )}
              </div>
            )}
            {transcriptError && <div className="transcript-upload-error">{transcriptError}</div>}
          </div>
        </div>

        {/* ── Physical ── */}
        <div className="edit-section">
          <h3 className="edit-section-title">Physical</h3>
          <div className="edit-row">
            <div className="edit-field">
              <label>Height</label>
              <input name="height" type="text" value={form.height} onChange={handleChange} placeholder="6'1&quot;" />
            </div>
            <div className="edit-field">
              <label>Weight</label>
              <input name="weight" type="text" value={form.weight} onChange={handleChange} placeholder="185 lbs" />
            </div>
          </div>
          <div className="edit-row">
            <div className="edit-field">
              <label>Bats</label>
              <select name="bats" value={form.bats} onChange={handleChange}>
                <option value="">—</option>
                <option value="L">Left</option>
                <option value="R">Right</option>
                <option value="S">Switch</option>
              </select>
            </div>
            <div className="edit-field">
              <label>Throws</label>
              <select name="throws" value={form.throws} onChange={handleChange}>
                <option value="">—</option>
                <option value="L">Left</option>
                <option value="R">Right</option>
              </select>
            </div>
          </div>
        </div>

        {/* ── About Me ── */}
        <div className="edit-section">
          <h3 className="edit-section-title">About Me</h3>
          <div className="edit-field">
            <div className="bio-label-row">
              <label>Bio</label>
              <span className={`bio-count${(form.bio ?? '').length >= 280 ? ' bio-count-warn' : ''}`}>
                {(form.bio ?? '').length}/300
              </span>
            </div>
            <textarea
              name="bio"
              value={form.bio}
              onChange={handleChange}
              maxLength={300}
              rows={4}
              placeholder="Tell coaches about yourself, your goals, and what makes you stand out…"
            />
          </div>
        </div>

        {/* ── High School Coach ── */}
        <div className="edit-section">
          <h3 className="edit-section-title">High School Coach</h3>
          <div className="edit-row">
            <div className="edit-field">
              <label>First Name</label>
              <input name="hs_coach_first_name" type="text" value={form.hs_coach_first_name} onChange={handleChange} placeholder="First" />
            </div>
            <div className="edit-field">
              <label>Last Name</label>
              <input name="hs_coach_last_name" type="text" value={form.hs_coach_last_name} onChange={handleChange} placeholder="Last" />
            </div>
          </div>
          <div className="edit-row">
            <div className="edit-field">
              <label>Email</label>
              <input name="hs_coach_email" type="email" value={form.hs_coach_email} onChange={handleChange} placeholder="coach@school.edu" />
            </div>
            <div className="edit-field">
              <label>Phone</label>
              <input name="hs_coach_phone" type="tel" value={form.hs_coach_phone} onChange={handleChange} placeholder="(555) 000-0000" />
            </div>
          </div>
        </div>

        {/* ── Travel Coach ── */}
        <div className="edit-section">
          <h3 className="edit-section-title">Travel Coach</h3>
          <div className="edit-row">
            <div className="edit-field">
              <label>First Name</label>
              <input name="travel_coach_first_name" type="text" value={form.travel_coach_first_name} onChange={handleChange} placeholder="First" />
            </div>
            <div className="edit-field">
              <label>Last Name</label>
              <input name="travel_coach_last_name" type="text" value={form.travel_coach_last_name} onChange={handleChange} placeholder="Last" />
            </div>
          </div>
          <div className="edit-row">
            <div className="edit-field">
              <label>Email</label>
              <input name="travel_coach_email" type="email" value={form.travel_coach_email} onChange={handleChange} placeholder="coach@org.com" />
            </div>
            <div className="edit-field">
              <label>Phone</label>
              <input name="travel_coach_phone" type="tel" value={form.travel_coach_phone} onChange={handleChange} placeholder="(555) 000-0000" />
            </div>
          </div>
        </div>

        {/* ── Highlight Video (upload) ── */}
        <div className="edit-section">
          <h3 className="edit-section-title">Highlight Video</h3>
          <div className="video-upload-wrap">
            {form.video_url ? (
              <div className="video-current">
                <div className="video-current-info">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                  </svg>
                  <span className="video-filename">{displayedFileName}</span>
                </div>
                <button
                  className="btn-remove-video"
                  onClick={handleRemoveVideo}
                  disabled={removingVideo}
                >
                  {removingVideo ? 'Removing…' : 'Remove'}
                </button>
              </div>
            ) : (
              <label
                className={`video-upload-label${uploading ? ' uploading' : ''}`}
                htmlFor="video-file-input"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 16 12 12 8 16"/>
                  <line x1="12" y1="12" x2="12" y2="21"/>
                  <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                </svg>
                <span className="video-upload-label-text">
                  {uploading ? 'Uploading…' : 'Click to upload video'}
                </span>
                <span className="video-upload-hint">.mp4, .mov, .webm</span>
              </label>
            )}

            <input
              ref={videoInputRef}
              id="video-file-input"
              type="file"
              accept=".mp4,.mov,.webm,video/mp4,video/quicktime,video/webm"
              onChange={handleVideoUpload}
              disabled={uploading || !!form.video_url}
              style={{ display: 'none' }}
            />

            {uploading && (
              <div className="video-progress-wrap">
                <div className="video-progress-bar">
                  <div
                    className="video-progress-fill"
                    style={{ width: `${uploadProgress || 5}%` }}
                  />
                </div>
                <span className="video-progress-label">
                  {uploadProgress > 0 ? `${uploadProgress}%` : 'Uploading…'}
                </span>
              </div>
            )}

            {uploadError && (
              <div className="video-upload-error">{uploadError}</div>
            )}
          </div>
        </div>

        {/* ── Recruiting Profiles ── */}
        <div className="edit-section">
          <h3 className="edit-section-title">Recruiting Profiles</h3>
          <div className="edit-field">
            <label>Perfect Game</label>
            <input
              name="perfect_game_url"
              type="url"
              value={form.perfect_game_url}
              onChange={handleChange}
              placeholder="https://www.perfectgame.org/..."
            />
          </div>
          <div className="edit-field">
            <label>Prep Baseball Report</label>
            <input
              name="prep_baseball_url"
              type="url"
              value={form.prep_baseball_url}
              onChange={handleChange}
              placeholder="https://www.prepbaseballreport.com/..."
            />
          </div>
        </div>

        {/* ── Social ── */}
        <div className="edit-section">
          <h3 className="edit-section-title">Social</h3>
          <div className="edit-row">
            <div className="edit-field">
              <label>Instagram</label>
              <input name="instagram" type="text" value={form.instagram} onChange={handleChange} placeholder="@handle" />
            </div>
            <div className="edit-field">
              <label>Twitter / X</label>
              <input name="twitter" type="text" value={form.twitter} onChange={handleChange} placeholder="@handle" />
            </div>
          </div>
        </div>

        {saveError && <div className="mypage-error">{saveError}</div>}

        <button className="btn-save-profile" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Profile'}
        </button>
      </div>

      {/* ──────────────────── RIGHT: LIVE PREVIEW ──────────────────── */}
      <div className="mypage-preview-col">
        <p className="preview-label">Live Preview</p>

        <div className={`profile-card${!form.is_public ? ' profile-card-private' : ''}`}>

          {/* Privacy banner */}
          {!form.is_public && (
            <div className="profile-private-banner">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              Profile is private — only you can see this
            </div>
          )}

          {/* Header */}
          <div className="profile-header">
            <div className="profile-avatar">
              {form.avatar_url ? (
                <img src={form.avatar_url} alt={form.full_name || 'Profile'} className="profile-avatar-img" />
              ) : (
                avatarInitials(form.full_name)
              )}
            </div>
            <div className="profile-header-info">
              <h2 className="profile-name">
                {form.full_name || <span className="profile-placeholder">Your Name</span>}
              </h2>
              <div className="profile-tags">
                {form.grad_year && (
                  <span className="profile-tag profile-tag-year">
                    '{String(form.grad_year).slice(-2)}
                  </span>
                )}
                {form.position && (
                  <span className="profile-tag profile-tag-pos">{form.position}</span>
                )}
                {(form.high_school || form.hometown) && (
                  <span className="profile-tag profile-tag-location">
                    {[form.high_school, form.hometown].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
            </div>
            {form.contact_email && (
              <a href={`mailto:${form.contact_email}`} className="profile-contact-btn">
                📧 Contact Me
              </a>
            )}
          </div>

          {/* Physical stats */}
          {(form.height || form.weight || form.bats || form.throws) && (
            <div className="profile-stat-row">
              {form.height && <div className="profile-stat"><span className="stat-label">HT</span><span className="stat-val">{form.height}</span></div>}
              {form.weight && <div className="profile-stat"><span className="stat-label">WT</span><span className="stat-val">{form.weight}</span></div>}
              {form.bats   && <div className="profile-stat"><span className="stat-label">B</span><span className="stat-val">{form.bats}</span></div>}
              {form.throws && <div className="profile-stat"><span className="stat-label">T</span><span className="stat-val">{form.throws}</span></div>}
            </div>
          )}

          {/* Academic stats */}
          <div className="profile-stat-row profile-stat-row-academic">
            {form.gpa && <div className="profile-stat"><span className="stat-label">GPA</span><span className="stat-val">{form.gpa}</span></div>}
            <div className="profile-stat"><span className="stat-label">SAT</span><span className="stat-val">{form.sat_score}</span></div>
            <div className="profile-stat"><span className="stat-label">ACT</span><span className="stat-val">{form.act_score}</span></div>
          </div>

          {/* Bio */}
          {form.bio && (
            <div className="profile-bio">
              <p>{form.bio}</p>
            </div>
          )}

          {/* Uploaded video */}
          {form.video_url && (
            <div className="profile-video">
              <video controls src={form.video_url} preload="metadata" />
            </div>
          )}

          {/* Coaches */}
          {(form.hs_coach_last_name || form.travel_coach_last_name) && (
            <div className="profile-coaches">
              <h4 className="profile-section-heading">Coaches</h4>
              <div className="profile-coaches-grid">
                {form.hs_coach_last_name && (
                  <div className="coach-contact-card">
                    <span className="coach-type-label">High School Coach</span>
                    <span className="coach-full-name">
                      {[form.hs_coach_first_name, form.hs_coach_last_name].filter(Boolean).join(' ')}
                    </span>
                    {form.hs_coach_email && (
                      <a href={`mailto:${form.hs_coach_email}`} className="coach-contact-link">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                          <polyline points="22,6 12,13 2,6"/>
                        </svg>
                        {form.hs_coach_email}
                      </a>
                    )}
                    {form.hs_coach_phone && (
                      <a href={`tel:${form.hs_coach_phone}`} className="coach-contact-link">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.62 3.36 2 2 0 0 1 3.59 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                        {form.hs_coach_phone}
                      </a>
                    )}
                  </div>
                )}
                {form.travel_coach_last_name && (
                  <div className="coach-contact-card">
                    <span className="coach-type-label">Travel Coach</span>
                    <span className="coach-full-name">
                      {[form.travel_coach_first_name, form.travel_coach_last_name].filter(Boolean).join(' ')}
                    </span>
                    {form.travel_coach_email && (
                      <a href={`mailto:${form.travel_coach_email}`} className="coach-contact-link">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                          <polyline points="22,6 12,13 2,6"/>
                        </svg>
                        {form.travel_coach_email}
                      </a>
                    )}
                    {form.travel_coach_phone && (
                      <a href={`tel:${form.travel_coach_phone}`} className="coach-contact-link">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.62 3.36 2 2 0 0 1 3.59 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                        {form.travel_coach_phone}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recruiting profile badges */}
          {(form.perfect_game_url || form.prep_baseball_url) && (
            <div className="profile-recruiting-badges">
              {form.perfect_game_url && (
                <a
                  href={form.perfect_game_url}
                  className="recruiting-badge recruiting-badge-pg"
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  Perfect Game Profile
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              )}
              {form.prep_baseball_url && (
                <a
                  href={form.prep_baseball_url}
                  className="recruiting-badge recruiting-badge-pbr"
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 8v4l3 3"/>
                  </svg>
                  Prep Baseball Report
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              )}
            </div>
          )}

          {/* Social */}
          {(form.instagram || form.twitter) && (
            <div className="profile-social">
              {form.instagram && (
                <a href={`https://instagram.com/${form.instagram.replace(/^@/, '')}`} className="profile-social-link profile-social-ig" target="_blank" rel="noreferrer">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5"/>
                    <circle cx="12" cy="12" r="4"/>
                    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
                  </svg>
                  @{form.instagram.replace(/^@/, '')}
                </a>
              )}
              {form.twitter && (
                <a href={`https://x.com/${form.twitter.replace(/^@/, '')}`} className="profile-social-link profile-social-x" target="_blank" rel="noreferrer">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  @{form.twitter.replace(/^@/, '')}
                </a>
              )}
            </div>
          )}
        </div>

        {/* Public link */}
        {form.slug ? (
          <div className="profile-public-link">
            <span className="public-link-label">Your public link</span>
            <div className="public-link-row">
              <span className="public-link-url">commithq.app/athlete/{form.slug}</span>
              <button className={`btn-copy-link${copied ? ' copied' : ''}`} onClick={handleCopy}>
                {copied ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Copied
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <p className="profile-link-hint">Save your profile to generate your public link.</p>
        )}
      </div>

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  )
}

function Toast({ message, onDone }) {
  const t = useRef(null)
  useEffect(() => {
    t.current = setTimeout(onDone, 3200)
    return () => clearTimeout(t.current)
  }, [onDone])
  return (
    <div className="mypage-toast">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
      {message}
    </div>
  )
}
